import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import './PushNotificationForm.css';

const PushNotificationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    data: '',
    target: 'all',
    targetUsers: [],
    topic: '',
    scheduledAt: ''
  });
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCustomers();
    if (isEdit) {
      fetchNotification();
    }
  }, [id]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/customers`, { params: { limit: 1000 } });
      setCustomers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchNotification = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/push-notifications/${id}`);
      const notification = response.data.data;
      setFormData({
        title: notification.title || '',
        message: notification.message || '',
        data: notification.data ? JSON.stringify(notification.data, null, 2) : '',
        target: notification.target || 'all',
        targetUsers: notification.targetUsers?.map(u => typeof u === 'object' ? u._id : u) || [],
        topic: notification.topic || '',
        scheduledAt: notification.scheduledAt ? new Date(notification.scheduledAt).toISOString().slice(0, 16) : ''
      });
      if (notification.image) {
        setImagePreview(`${API_BASE_URL_WITHOUT_API}${notification.image}`);
      }
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch notification');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'targetUsers') {
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
      setFormData({
        ...formData,
        targetUsers: selectedOptions
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('message', formData.message);
      if (formData.data) {
        submitData.append('data', formData.data);
      }
      submitData.append('target', formData.target);
      submitData.append('targetUsers', JSON.stringify(formData.targetUsers));
      submitData.append('topic', formData.topic);
      if (formData.scheduledAt) {
        submitData.append('scheduledAt', new Date(formData.scheduledAt).toISOString());
      }
      if (imageFile) {
        submitData.append('image', imageFile);
      }

      if (isEdit) {
        await axios.put(`${API_BASE_URL}/admin/push-notifications/${id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API_BASE_URL}/admin/push-notifications`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      navigate('/sy-admin/push-notifications');
    } catch (error) {
      setError(error.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} notification`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="notification-form-container">
          <div className="notification-form-header">
            <div className="skeleton skeleton-text" style={{ width: '300px', height: '40px', borderRadius: '8px' }}></div>
            <div className="skeleton skeleton-button" style={{ width: '180px', height: '40px', borderRadius: '8px' }}></div>
          </div>
          <div className="notification-form-card">
            <div className="form-section">
              <div className="skeleton skeleton-text" style={{ width: '200px', height: '32px', borderRadius: '8px', marginBottom: '24px' }}></div>
              <div className="form-group">
                <div className="skeleton skeleton-text" style={{ width: '100px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="notification-form-container">
        <div className="notification-form-header">
          <h1>{isEdit ? 'Edit Push Notification' : 'Create New Push Notification'}</h1>
          <button
            onClick={() => navigate('/sy-admin/push-notifications')}
            className="btn btn-secondary"
          >
            ← Back to Notifications
          </button>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div className="notification-form-card">
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h2>Notification Details</h2>
              
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  placeholder="Notification title"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  placeholder="Notification message"
                />
              </div>

              <div className="form-group">
                <label htmlFor="image">Image</label>
                {imagePreview && (
                  <div className="image-preview-container">
                    <img src={imagePreview} alt="Preview" className="notification-image-preview" />
                  </div>
                )}
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="target">Target *</label>
                <select
                  id="target"
                  name="target"
                  value={formData.target}
                  onChange={handleChange}
                  required
                >
                  <option value="all">All Users</option>
                  <option value="specific">Specific Users</option>
                  <option value="topic">Topic</option>
                </select>
              </div>

              {formData.target === 'specific' && (
                <div className="form-group">
                  <label htmlFor="targetUsers">Select Users *</label>
                  <select
                    id="targetUsers"
                    name="targetUsers"
                    multiple
                    value={formData.targetUsers}
                    onChange={handleChange}
                    required
                    className="multi-select"
                    size="5"
                  >
                    {customers.map(customer => (
                      <option key={customer._id} value={customer._id}>
                        {customer.displayName || customer.name || customer.phone || customer.email || 'Customer'}
                      </option>
                    ))}
                  </select>
                  <small>Hold Ctrl/Cmd to select multiple users</small>
                </div>
              )}

              {formData.target === 'topic' && (
                <div className="form-group">
                  <label htmlFor="topic">Topic *</label>
                  <input
                    type="text"
                    id="topic"
                    name="topic"
                    value={formData.topic}
                    onChange={handleChange}
                    required
                    placeholder="e.g., promotions, updates"
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="data">Additional Data (JSON)</label>
                <textarea
                  id="data"
                  name="data"
                  value={formData.data}
                  onChange={handleChange}
                  rows="4"
                  placeholder='{"key": "value"}'
                />
                <small>Optional JSON data to send with notification</small>
              </div>

              <div className="form-group">
                <label htmlFor="scheduledAt">Schedule (Optional)</label>
                <input
                  type="datetime-local"
                  id="scheduledAt"
                  name="scheduledAt"
                  value={formData.scheduledAt}
                  onChange={handleChange}
                />
                <small>Leave empty to send immediately or save as draft</small>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : (isEdit ? 'Update Notification' : 'Create Notification')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/sy-admin/push-notifications')}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default PushNotificationForm;
